import api from 'api';
import * as hoursApi from 'api/hours/hours.api';
import reportsApi from 'api/reports/reports.api';
import type { BreakdownType } from 'components/datatable/datatable.types';
import type { ExportWorksheet } from 'components/superreports/ReportExport/types';
import type { HourColumn, SupportedExtensions, TaskColumn } from 'constants/export';
import { PREFERRED_REPORT_FILTER, PREFERRED_REPORT_VIEW } from 'constants/user';
import { WIDGET_PROJECTS_LIST } from 'constants/widgets';
import { errorMessageFactory } from 'factories/api';
import { reportQueryFactory } from 'factories/filter';
import {
  dataQueryFilterRequestFactory,
  duplicateReportDraftFactory,
  type ExportPreviewRow,
  exportPreviewHourFactory,
  hourQueryFilterRequestFactory,
  newReportDraftFactory,
  newReportWidgetFactory,
  saveReportDraftRequestFactory,
} from 'factories/superreports';
import { isEmpty, uniq } from 'lodash-es';
import * as hourSummaryTypes from 'redux/modules/hourSummary.types';
import { HOUR_SUMMARY_INITIAL_FILTER } from 'redux/modules/hourSummary.types';
import * as hoursTypes from 'redux/modules/hours.types';
import { HOUR_DETAILS_INITIAL_FILTER } from 'redux/modules/hours.types';
import { getTagEntities } from 'redux/modules/tags.selectors';
import { showToast } from 'redux/modules/toasts.actions';
import { saveUserProperty } from 'redux/modules/user.actions';
import { isAbortError, xhrStatusQueryInfo } from 'redux/reusable/xhrStatus';
import { getAccountFeatures, getStartOfWeek } from 'redux/selectors/accounts';
import { getCurrenciesById } from 'redux/selectors/currencies';
import { getHourEntities, getHourResult, getHours, getHoursAsCards } from 'redux/selectors/hours';
import { getAllLoadedProjects } from 'redux/selectors/projects';
import {
  getFilteredQuery,
  getFilteredQueryInApiFormat,
  getPublicReportFilters,
  getReportDraft,
  getReportFilters,
  getReportQueryData,
  getReportSelection,
  getReports,
  getRequiredEntitiesForReport,
  getWidgetMetadata,
  getXhrStatus,
} from 'redux/selectors/superreports';
import { getReportBatchActionsData } from 'redux/selectors/superreports.batch';
import { getTeams } from 'redux/selectors/teams';
import { getUserId, getUserTimeFormat } from 'redux/selectors/user';
import { cachedFromISO } from 'redux/selectors/utils/capacities';
import type { IState } from 'redux/state';
import { forecastApi } from 'routes/forecast/api/forecast.api';
import type { Forecast } from 'routes/forecast/types/forecast';
import { HOUR_SCHEMA_KEY } from 'schema/hours';
import { TrackingActions } from 'tracking/actions';
import type { Hour, NormalizedHourToApi } from 'types/hour';
import { HourLockedReasons } from 'types/hour';
import type { HoursDetailsFilter } from 'types/hours';
import type { LightProject, Project } from 'types/project';
import type { Thunk, ThunkDispatch } from 'types/redux';
import type {
  DownloadedReportFile,
  EntityDataResponse,
  PublicReportEntity,
  ReportEntity,
  SharedReportsFilter,
} from 'types/reports';
import { EntityDataKey } from 'types/reports';
import type { DeepPartial } from 'types/utility';
import debug from 'utils/debug';
import { ZERO_DURATION } from 'utils/durations';
import {
  createEmptyEntityDataResponse,
  excludeReadonlyHours,
  getLocalStorageTimesheetColumnSettingsKey,
  getSummaryFromTotals,
  transformExportedReportColumns,
} from 'utils/superreports';
import { attachParentIdToChildTagsIds } from 'utils/tags';
import { keyBy, notNull, orderBy, uniqBy } from 'utils/typesafe';
import type { ColumnModeOptions } from './accounts.types';
import { fetchCurrencies } from './currencies';
import { fetchDayPropertiesSmartly } from './dayProperties';
import type { DayPropertiesFilter } from './dayProperties.schema';
import { getHourStates, getNonDeletedHourStates } from './hourStates';
import { defaultState } from './hourStates.constants';
import type { HourSummary } from './hourSummary.types';

// Lazy import to break circular dependency chain through hours.actions
const getFetchHourIds = () => import('./hours.actions').then(m => m.fetchHourIds);

import { initialState } from './superreports';
import type { EmailedReport, ReportFilterTypes, ReportViewTypes } from './superreports.types';
import * as actionTypes from './superreports.types';
import { updateReportDraft } from './superreports.types';
import type { HourSummaryRow } from './superreports-datatable.types';

const TIMESHEET_FETCH_KEY = 'timesheet';

export function setQuery(
  query: DeepPartial<actionTypes.ReportQuery>,
  options: { skipFetch?: boolean } = { skipFetch: false }
): Thunk<Promise<unknown>> {
  return async dispatch => {
    const { skipFetch } = options;
    // Reset widgets to initial _status
    dispatch(actionTypes.resetWidgetStatusOnDraft());

    // Clear hours store, as hours don't match filters anymore
    dispatch(hoursTypes.clearHours());

    // Clear all selections, so as we don't end up with "invisible" ones that can no longer be removed
    dispatch(actionTypes.batchSelectionActions.clearSelection());

    dispatch(actionTypes.setQuery(query));

    if (skipFetch) {
      return;
    }

    await dispatch(fetchReportData(true));
  };
}

// Sets query/filters to include all labels/users/projects
export function setDefaultQuery(
  seed: DeepPartial<actionTypes.ReportQuery> = {},
  options: { skipFetch?: boolean } = { skipFetch: false }
): Thunk {
  return (dispatch, getState) => {
    const { skipFetch } = options;
    const weekStartsOn = getStartOfWeek(getState());
    const query = reportQueryFactory(seed, { defaultValues: true, weekStartsOn });
    dispatch(setQuery(query, { skipFetch }));
  };
}

// Draft stuff
export function createReportDraft(
  seed: Partial<actionTypes.ReportDraft> = {},
  querySeed = {}
): Thunk<actionTypes.ReportDraft> {
  return dispatch => {
    const draft = newReportDraftFactory(seed);

    dispatch(actionTypes.createReportDraft(draft));
    dispatch(setDefaultQuery(querySeed, { skipFetch: true }));

    return draft;
  };
}

export function fetchReportById(id: number): Thunk<Promise<ReportEntity | undefined>> {
  return async (dispatch, getState) => {
    try {
      const key = `report-${id}`;
      const xhrStatus = xhrStatusQueryInfo(getXhrStatus(getState()), { key });

      if (!xhrStatus.shouldFetch) {
        return;
      }
      const report = await dispatch(
        actionTypes.wrapFetching(reportsApi.getReportById(id), `report-${id}`)
      );

      dispatch(actionTypes.fetchedReport(report));
      return report;
    } catch (e) {
      throw Error(e);
    }
  };
}

// Loads an existing report info draft
export function createReportDraftFromId(
  reportId: number,
  asDuplicate: boolean = false
): Thunk<Promise<ReportEntity>> {
  return async (dispatch, getState) => {
    const state = getState();
    // Need this to choose correct "week start-end" when initializing query filters
    const weekStartsOn = getStartOfWeek(state);
    // Get report from server (since we don't have .widgets info locally)
    const report = await dispatch(
      actionTypes.wrapFetching(reportsApi.getReportById(reportId), 'report')
    );

    let draft: actionTypes.ReportDraft;
    if (asDuplicate) {
      draft = duplicateReportDraftFactory(report);
    } else {
      draft = newReportDraftFactory(report);
    }

    draft.exportFields = report.hour_fields as Array<HourColumn>;
    if (report.task_fields) {
      draft.taskExportFields = report.task_fields as Array<TaskColumn>;
    }

    // Load draft
    dispatch(actionTypes.createReportDraft(draft));
    // Set (=run) data query
    // Only use relativeDates for reports with dynamic date ranges (e.g., 'this_month', 'prev_week')
    // Reports with fixed dates (from/to set, no date_range) should preserve their original dates
    const hasFixedDates = report.from && report.to && !report.date_range;
    const query = reportQueryFactory(report, { relativeDates: !hasFixedDates, weekStartsOn });

    // Clear stale hours from any previously loaded report so hourQueryFilterRequestFactory
    // calculates offset=0 and doesn't skip results on expansion
    dispatch(hoursTypes.clearHours());
    dispatch(actionTypes.setQuery(query));

    // Re-set signature that tracks unsaved changes
    dispatch(actionTypes.setDraftSignature());

    return report;
  };
}

// Duplicates an existing report
export function duplicateReportIdToDraft(reportId: number): Thunk<Promise<unknown>> {
  return dispatch => {
    return dispatch(createReportDraftFromId(reportId, true));
  };
}

/**
 * Duplicates the current report draft.
 */
export const duplicateCurrentReportDraft = (): Thunk => (dispatch, getState) => {
  const draft = getReportDraft(getState());
  const duplicate = duplicateReportDraftFactory(draft);
  return dispatch(actionTypes.createReportDraft(duplicate));
};

/**
 * Fetches data for a report and merges it with any existing data.
 * @param reload If `true`, data will be replaced instead of merged (for example when the report
 * query changes).
 * @param entities List of entities that should always be included, regardless of whether they are
 * required by the current report widgets.
 */
export function fetchReportData(reload?: boolean, entities?: Array<EntityDataKey>): Thunk {
  return async (dispatch, getState) => {
    // We need to abort any in-flight requests for the timesheet hours, as expanded report table
    // rows share the same state slice and pagination for hours will break if the timesheet hours
    // are injected into the store before loading report hours
    actionTypes.abortFetching(TIMESHEET_FETCH_KEY);

    let state = getState();
    const xhrStatus = getXhrStatus(state);

    const filteredQuery = getFilteredQueryInApiFormat(state);

    const query = dataQueryFilterRequestFactory(filteredQuery);

    const requiredEntities = getRequiredEntitiesForReport(state);
    let entitiesToLoad = reload
      ? requiredEntities
      : requiredEntities.filter(entity => !xhrStatus?.fetched.find(({ key }) => key === entity));

    if (entities?.length) {
      entitiesToLoad = uniq(entitiesToLoad.concat(entities));
    }

    if (reload) {
      dispatch(actionTypes.clearEntityFetchStatus());
    }

    // Abort any stale requests
    entitiesToLoad.forEach(entity => {
      actionTypes.abortFetching(entity);
    });

    try {
      let requests: Array<Promise<EntityDataResponse>> = [];
      // If there are required entities to load, fetch them
      if (entitiesToLoad.length) {
        requests = entitiesToLoad.map(entity => {
          return dispatch(
            actionTypes.wrapFetching(
              abortSignal =>
                reportsApi.getReportData(
                  { ...query, group_only: entity !== 'totals' },
                  entity === 'totals' ? [] : [entity],
                  abortSignal
                ),
              entity
            )
          );
        });
      }

      // Everything is already fetched, so no need to re-fetch
      else {
        return;
      }

      const responses = await Promise.all(requests);
      const currencyLookup = getCurrenciesById(state);
      const data = responses.reduce(
        (temp, response, index) => {
          // Track which entity this response is for using the index
          const entity = entitiesToLoad[index];
          return {
            clients: uniqBy(temp.clients.concat(response.clients), 'id'),
            days: temp.days.concat(response.days),
            labels: uniqBy(temp.labels.concat(response.labels), 'id'),
            teams: temp.teams.concat(response.teams),
            users: uniqBy(temp.users.concat(response.users), 'id'),
            linked_work: uniqBy(temp.linked_work.concat(response.linked_work), 'id'),
            // Only update totals from the totals entity response to avoid using stale cached data
            totals:
              entity === 'totals'
                ? getSummaryFromTotals(response.totals, currencyLookup)
                : temp.totals,
            multiple_currency_report:
              temp.multiple_currency_report || response.multiple_currency_report,
          };
        },
        { ...createEmptyEntityDataResponse(), totals: state.superreports.totals }
      );

      if (data.multiple_currency_report) {
        delete data.totals.currency;
      }

      dispatch(actionTypes.fetchedReportData({ data, reload }));

      state = getState();

      if (reload) {
        dispatch(actionTypes.synthesizeProjectsFromHours(getHours(state)));
      }
    } catch (error) {
      if (isAbortError(error)) return;

      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      return error;
    }
  };
}

export interface FetchHoursForParams {
  expandRow: boolean;
  filterScope: actionTypes.HoursFilterScope;
  id: number;
  uri?: string; // For linked_work widget, we use URI instead of ID
  widgetReference: number | string;
}

// Report hours
export function fetchReportHoursFor(params: FetchHoursForParams): Thunk {
  return async (dispatch, getState) => {
    // ABORT MISSION, if we are collapsing the row -- no fetching done
    if (!params.expandRow) {
      return dispatch(
        actionTypes.collapseRowOnWidget({
          widgetReference: params.widgetReference,
          rowId: params.id,
        })
      );
    }

    dispatch(
      actionTypes.expandAndLoadRowOnWidget({
        widgetReference: params.widgetReference,
        rowId: params.id,
      })
    );

    const state = getState();

    const filteredQuery = getFilteredQueryInApiFormat(state);

    const query = hourQueryFilterRequestFactory(filteredQuery, {
      ...params,
      hourEntities: getHourEntities(state),
      hourResult: getHourResult(state),
    });

    try {
      const data = await reportsApi.getReportHours(query);

      // Now that fetching has been split up, we can no longer assume the necessary entities are
      // already fetched. If they aren't, we need to load them to map and display hours correctly.
      const fetchedEntities = getXhrStatus(state)?.fetched;

      // We filter hours by projects, to ensure we only show hours for projects that
      // are part of the report (since hours can be loaded from other places, for example the
      // calendar view).
      dispatch(actionTypes.synthesizeProjectsFromHours(data));

      // Individual hours only include the tag IDs, so to be able to show their name/emoji and
      // correctly determine their parent, we need to load them as well.
      if (
        data.some(hour => hour.label_ids?.length) &&
        !fetchedEntities.find(s => s.key === EntityDataKey.Tags)
      ) {
        await dispatch(fetchReportData(false, [EntityDataKey.Tags]));
      }

      dispatch(
        actionTypes.fetchedWidgetHours({
          widgetReference: params.widgetReference,
          rowId: params.id,
          hasMoreResults: data.length === query.limit,
          hourIds: data.map(hour => hour.id),
        })
      );

      // Load hours into 'hours' store:
      dispatch(hoursTypes.injectHours(data));
      return data;
    } catch (error) {
      debug.log({ error });
      dispatch(
        showToast({
          message: 'An error occurred while retrieving hours',
          type: 'danger',
        })
      );
      dispatch(
        actionTypes.fetchWidgetHoursError({
          widgetReference: params.widgetReference,
          rowId: params.id,
        })
      );
      return error;
    }
  };
}

// Public Reports
export function fetchPublicReports(
  filter?: Partial<SharedReportsFilter>
): Thunk<Promise<Array<PublicReportEntity>>> {
  return async (dispatch, getState) => {
    try {
      const stateFilter = getPublicReportFilters(getState());
      const data = await dispatch(
        actionTypes.wrapFetching(
          reportsApi.getPublicReports({ ...stateFilter, ...filter }),
          'public-reports'
        )
      );
      dispatch(actionTypes.fetchedPublicReports(data));
      return data;
    } catch (e) {
      throw Error(e);
    }
  };
}

export function initializePublicReport(
  publicReportId: string
): Thunk<Promise<actionTypes.ReportDraft>> {
  return async (dispatch, getState) => {
    window.BOOTSTRAPPED_DATA.standaloneClient = true;

    const [report] = await Promise.all([
      dispatch(fetchPublicReport(publicReportId)),
      dispatch(fetchPublicReportData(publicReportId)),
      dispatch(fetchWidgetMetadata()),
    ]);

    dispatch(actionTypes.setPublicReportTimestamp(report.created_at));
    const weekStartsOn = getStartOfWeek(getState());
    dispatch(actionTypes.setQuery(reportQueryFactory(report, { weekStartsOn })));
    return getReportDraft(getState());
  };
}

export function fetchReports(reload?: boolean): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const reportsFilter = getReportFilters(state);
    const data = await dispatch(
      actionTypes.wrapFetching(reportsApi.getReports(reportsFilter), 'reports')
    );
    dispatch(actionTypes.fetchedReports({ data, reload }));
  };
}

export function loadTimesheetColumnsFromLocalStorage(userId: number): Thunk {
  return dispatch => {
    const key = getLocalStorageTimesheetColumnSettingsKey(userId);
    const columnsString = localStorage.getItem(key);

    if (columnsString !== null) {
      try {
        const columns = JSON.parse(columnsString);
        dispatch(actionTypes.setTimesheetColumns(columns));
      } catch {}
    }
  };
}

export function updateTimesheetColumns(columns: Array<HourColumn>): Thunk {
  return (dispatch, getState) => {
    dispatch(actionTypes.setTimesheetColumns(columns));
    const state = getState();
    const userId = getUserId(state);
    const key = getLocalStorageTimesheetColumnSettingsKey(userId);
    localStorage.setItem(key, JSON.stringify(columns));
  };
}

export function updateReportDraftColumns(columns: {
  exportFields: Array<HourColumn>;
  taskExportFields: Array<TaskColumn>;
}): Thunk {
  return dispatch => {
    dispatch(
      updateReportDraft({
        exportFields: columns.exportFields,
        taskExportFields: columns.taskExportFields,
      })
    );
  };
}

export function saveReportDraft(): Thunk<Promise<ReportEntity | undefined>> {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = getReportDraft(state);
    const query = getFilteredQuery(state);
    const metadata = getWidgetMetadata(state);
    const payload = saveReportDraftRequestFactory(draft, query, metadata);

    try {
      const data = await dispatch(actionTypes.wrapSaving(reportsApi.saveReport(payload)));

      if (payload.id) {
        dispatch(actionTypes.updatedExistingReport(data));
      } else {
        dispatch(actionTypes.createdNewReport(data));
        dispatch(updateReportDraft({ id: data.id }));
      }

      // Re-set signature that tracks unsaved changes
      dispatch(actionTypes.setDraftSignature());

      return data;
    } catch (error) {
      dispatch(showToast({ message: errorMessageFactory(error), type: 'danger' }));
    }
  };
}

export function shareReportDraft(): Thunk<Promise<PublicReportEntity | undefined>> {
  return async (dispatch, getState) => {
    // Save the draft to server so we include all unsaved changes first
    const report = await dispatch(saveReportDraft());
    if (!report) return;

    const state = getState();
    const draft = getReportDraft(state);
    const isLiveReportsEnabled = !!getAccountFeatures(state).live_reports;

    const sharedPayload: Partial<PublicReportEntity> = {
      name: draft.name,
      description: draft.description,
      events: !!draft.live && !!draft.events && isLiveReportsEnabled,
      live: !!draft.live && isLiveReportsEnabled,
    };

    const sharedResponse = await dispatch(
      actionTypes.wrapSaving(reportsApi.shareReportId(report.id, sharedPayload), 'public-report')
    );

    dispatch(actionTypes.createdNewPublicReport(sharedResponse));
    return sharedResponse;
  };
}

export function shareReportId(reportId: number): Thunk<Promise<PublicReportEntity | undefined>> {
  return async dispatch => {
    try {
      await dispatch(createReportDraftFromId(reportId));
    } catch {
      dispatch(actionTypes.clearReportDraft());
      dispatch(showToast({ message: `Public Report could not be shared`, type: 'danger' }));
      return;
    }

    dispatch(
      updateReportDraft({
        live: false,
        events: false,
      })
    );

    const sharedResponse = await dispatch(shareReportDraft());

    dispatch(showToast({ message: `Report shared publicly`, type: 'info' }));
    return sharedResponse;
  };
}

/**
 * @param deletePublicReports If true, all public reports based on this report will also be deleted.
 */
export function deleteReportId(
  reportId: number,
  deletePublicReports?: boolean
): Thunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const publicReportIdsToDelete =
      getReports(state).find(r => r.id === reportId)?.publicReportIds ?? [];
    const publicReportsFilter = getPublicReportFilters(state);
    dispatch(actionTypes.markReportDeleted(reportId));

    return reportsApi
      .deleteReport(reportId)
      .then(async () => {
        dispatch(actionTypes.deleteReport(reportId));

        if (deletePublicReports) {
          const publicReportResults = await Promise.allSettled(
            publicReportIdsToDelete.map(id => dispatch(deletePublicReportId(id)))
          );

          if (publicReportResults.some(({ status }) => status === 'rejected')) {
            dispatch(
              showToast({
                message:
                  'Some shared reports could not be deleted, and will need to be deleted manually.',
                type: 'danger',
              })
            );
          }

          // Re-fetch reports and public reports to work around a race condition due to them both
          // being loaded on mount in SuperReportsRoute
          dispatch(fetchReports());
          dispatch(fetchPublicReports(publicReportsFilter));
        }
      })
      .catch(error => {
        debug.log('error', error);
        dispatch(actionTypes.markReportUndeleted(reportId));
        return error;
      });
  };
}

export function deletePublicReportId(publicReportId: number): Thunk<Promise<void>> {
  return dispatch => {
    dispatch(actionTypes.deletePublicReport(publicReportId));
    return reportsApi.deletePublicReport(publicReportId).catch(() => {
      dispatch(actionTypes.undeletePublicReport(publicReportId));
    });
  };
}

export type ExportDownloadResponse = DownloadedReportFile | EmailedReport;

export function downloadReport(
  reportId: number,
  format: SupportedExtensions,
  options: {
    columns: Array<TaskColumn | HourColumn>;
    worksheets: Array<ExportWorksheet>;
    time_entries: boolean;
    show_excluded_columns: boolean;
    tags_mode: ColumnModeOptions;
    external_link_filter?: string;
    sync_status_filter?: string;
  }
): Thunk<Promise<ExportDownloadResponse>> {
  return async (dispatch, getState) => {
    const features = getAccountFeatures(getState());
    options.columns = transformExportedReportColumns(options.columns, features);

    TrackingActions.exportedReport(
      actionTypes.ExportReportTrackingSource.Report,
      format,
      options,
      reportId
    );
    const response = await dispatch(
      actionTypes.wrapSaving(reportsApi.exportReport(reportId, format, options))
    );
    return response;
  };
}

export type RemoveWidgetParams = Pick<actionTypes.WidgetDraft, 'sequence' | 'id' | '_ref'>;

export function removeReportWidgetFromDraft(widget: RemoveWidgetParams): Thunk {
  return dispatch => {
    dispatch(actionTypes.decreaseSequenceStartingWith(widget.sequence));
    dispatch(actionTypes.removeWidgetOnDraft(widget.id ?? widget._ref));
  };
}

export function addReportWidgetToDraft(
  seed: Partial<actionTypes.WidgetEntity> = { sequence: 0 }
): Thunk {
  return async (dispatch, getState) => {
    const state = getState();

    const sequence =
      seed.sequence ||
      state.superreports.draft.widgets.reduce(
        (tempSequence, current) => Math.max(tempSequence, current.sequence),
        1
      );

    const widget = newReportWidgetFactory({
      ...seed,
      widget_type: seed.widget_type ?? WIDGET_PROJECTS_LIST,
      sequence,
    });

    dispatch(actionTypes.increaseSequenceStartingWith(widget.sequence));
    dispatch(actionTypes.addWidgetToDraft(widget));
    await dispatch(fetchReportData());
  };
}

// Selection stuff
export function toggleReportView(type: ReportViewTypes): Thunk {
  return dispatch => {
    dispatch(saveUserProperty(PREFERRED_REPORT_VIEW, type));
    dispatch(actionTypes.togglePublicReportView(type));
  };
}

export function toggleReportListFilter(type: ReportFilterTypes): Thunk {
  return dispatch => {
    dispatch(saveUserProperty(PREFERRED_REPORT_FILTER, type));
    dispatch(actionTypes.publicReportListFilter(type));
  };
}

/** @param reportsContext If false, re-fetch the updated hours instead of report data on update. */
export function batchUpdateSelection({
  reportsContext = false,
  updateBillable = false,
  convertPlannedTimeToLogged = false,
  hasSelectedAllFiltered = false,
} = {}): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const state = getState();
    const hours = state.hours.entities[HOUR_SCHEMA_KEY];
    const { selection: fullSelection } = getReportBatchActionsData(state);
    const selection = excludeReadonlyHours(fullSelection, hours);
    const projects = getAllLoadedProjects(state);
    const { hasModifiedFilters } = getReportQueryData(state);
    const projectEntities: Record<number, Project | null | undefined> = {};
    const features = getAccountFeatures(state);
    const hasHourStatesFeature = !!features.hour_states;
    const hasBillingFeature = !!features.billing;
    const states = getNonDeletedHourStates(state);
    // Prepare updated dataset
    const updatedHours = selection.hourIds.map(hourId => {
      const hour = hours[hourId];
      const updatedHour: Partial<Hour<number>> = { id: hourId };

      if (selection.lockedState != null) {
        updatedHour.locked = selection.lockedState;
        updatedHour.locked_reason = selection.lockedState ? HourLockedReasons.Locked : null;
      }

      if (selection.projectId) {
        updatedHour.project = selection.projectId;
      }

      if (selection.labelIds.length) {
        updatedHour.label_ids = selection.labelIds.length
          ? attachParentIdToChildTagsIds(selection.labelIds, getTagEntities(state))
          : hour.label_ids;
      }

      if (selection.billedState !== undefined && selection.billedState !== null) {
        updatedHour.billed = selection.billedState;
      }

      if (updateBillable && updatedHour.project) {
        const project = (projectEntities[updatedHour?.project] ??= projects.find(
          project => project.base.id === updatedHour.project
        )?.fullProject);

        if (project) {
          updatedHour.billable = project.billable;
        }
      }

      if (convertPlannedTimeToLogged) {
        updatedHour.duration = hour.estimated_duration;
        updatedHour.estimated_duration = ZERO_DURATION;
      }

      // Only update state if the user explicitly selected a state (stateId is defined)
      // - undefined means "don't change state" (user didn't touch state dropdown)
      // - 0 means "clear state / set to No State"
      // - > 0 means "set to specific state"
      // (fixes TT-4326)
      if ((hasHourStatesFeature || hasBillingFeature) && selection.stateId !== undefined) {
        const updatedState = states[selection.stateId] ?? defaultState;
        updatedHour.state = updatedState !== defaultState ? updatedState : null;

        updatedHour.billed = updatedState.billed;
        updatedHour.locked = updatedState.locked;

        if (updatedState.locked) {
          updatedHour.locked_reason = HourLockedReasons.Locked;
        }
      }

      return updatedHour;
    });

    // In case we need to revert on a fail.
    const originalHours = selection.hourIds.map(hourId => hours[hourId]);

    // Optimistically update local state to reflect the changes we're making
    dispatch(
      hoursTypes.injectHours(
        updatedHours.map(updatedHour => {
          const originalHour = hours[updatedHour?.id ?? 0];
          return {
            ...originalHour,
            ...updatedHour,
          };
        })
      )
    );

    const numUpdated = updatedHours.length;

    if (numUpdated >= hoursTypes.BATCH_API_SYNC_LIMIT) {
      dispatch(
        showToast({
          message: `Updating ${numUpdated} ${numUpdated === 1 ? 'hour' : 'hours'}`,
          type: 'success',
        })
      );
      if (hasModifiedFilters) {
        dispatch(
          showToast({
            message: `The table will refresh after the update.`,
            type: 'success',
          })
        );
      }
    }

    // NB: hour.project holds reference to normalized project.id
    const payload: Array<NormalizedHourToApi> = updatedHours.map(
      ({
        id,
        billed,
        locked,
        locked_reason,
        project,
        label_ids,
        billable,
        duration,
        estimated_duration,
        state,
      }) => {
        const payload: Partial<NormalizedHourToApi> = {
          id,
          label_ids,
          project_id: project,
          billable,
        };
        if (hasHourStatesFeature || hasBillingFeature) {
          payload.state_id = state?.id ?? 0;
        }
        if (!hasHourStatesFeature) {
          payload.billed = billed;
          payload.locked = locked;
          payload.locked_reason = locked_reason;
        }

        if (convertPlannedTimeToLogged) {
          payload.hours = duration?.hours;
          payload.minutes = duration?.minutes;
          payload.estimated_hours = estimated_duration?.hours;
          payload.estimated_minutes = estimated_duration?.minutes;
        }

        return payload as NormalizedHourToApi;
      }
    );

    try {
      const results = await dispatch(actionTypes.wrapSaving(hoursApi.bulkUpdateHours(payload)));
      const updatedLength = results.updated_ids.length;
      dispatch(actionTypes.batchSelectionActions.clearSelection());
      // Explicitly reset stateId to undefined after update to prevent the previously selected
      // state from being pre-selected on the next update (fixes TT-4326)
      dispatch(
        actionTypes.batchSelectionActions.setSelection({
          selectionKey: 'stateId',
          value: undefined,
        })
      );

      if (numUpdated <= hoursTypes.BATCH_API_SYNC_LIMIT && updatedLength === 0) {
        dispatch(hoursTypes.injectHours(originalHours));
        dispatch(
          showToast({
            message: 'No entries updated',
            type: 'warning',
          })
        );
      } else if (numUpdated <= hoursTypes.BATCH_API_SYNC_LIMIT && !!updatedLength) {
        dispatch(
          showToast({
            message: `Updated ${updatedLength} ${updatedLength === 1 ? 'entry' : 'entries'}`,
            type: 'success',
          })
        );
        if (reportsContext) {
          dispatch(fetchReportData(true));
        } else if (selection.projectId) {
          getFetchHourIds().then(fetchHourIds => dispatch(fetchHourIds(results.updated_ids)));
        }

        if (hasModifiedFilters) {
          dispatch(setQuery({ page: 1 }));
          dispatch(fetchTimesheetData());
        }

        const hasErrors = results.errors.update.length > 0;

        if (hasErrors) {
          if (!hasModifiedFilters && !hasSelectedAllFiltered) {
            dispatch(fetchTimesheetData());
          }
        }
      }

      return results;
    } catch (error) {
      dispatch(hoursTypes.injectHours(originalHours));
      dispatch(showToast({ message: errorMessageFactory(error), type: 'danger' }));
    }
  };
}

export function batchDeleteSelection(): Thunk<Promise<unknown>> {
  return (dispatch, getState) => {
    const state = getState();
    const hours = getHourEntities(state);
    const { selection: fullSelection } = state.superreports;
    const selection = excludeReadonlyHours(fullSelection, hours);

    // Optimistically remove the hours we're removing
    const remainingHours = state.hours.result
      .filter(hourId => !selection.hourIds.includes(hourId))
      .map(hourId => hours[hourId]);

    // In case actionTypes.we need to revert on a fail.
    const originalHours = selection.hourIds.map(hourId => hours[hourId]);
    dispatch(actionTypes.batchSelectionActions.clearSelection());

    dispatch(hoursTypes.replaceHours(remainingHours));

    if (selection.hourIds.length >= hoursTypes.BATCH_API_SYNC_LIMIT) {
      const numUpdated = selection.hourIds.length;
      dispatch(
        showToast({
          message: `Deleted ${numUpdated} ${numUpdated === 1 ? 'hour' : 'hours'}`,
          type: 'success',
        })
      );
    }

    return dispatch(actionTypes.wrapSaving(hoursApi.bulkDeleteHours(selection.hourIds)))
      .then(res => {
        // Clear the selection
        dispatch(actionTypes.batchSelectionActions.clearSelection());
        if (originalHours.length <= hoursTypes.BATCH_API_SYNC_LIMIT) {
          // Re-fetch report data as sums are now wrong:
          dispatch(fetchReportData(true));

          // Notify
          const numUpdated = res.deleted_ids.length;
          dispatch(
            showToast({
              message: `Deleted ${numUpdated} ${numUpdated === 1 ? 'hour' : 'hours'}`,
              type: 'success',
            })
          );
        }

        return res;
      })
      .catch(error => {
        dispatch(hoursTypes.injectHours(originalHours));
        dispatch(
          actionTypes.batchSelectionActions.setSelection({
            selectionKey: 'hourIds',
            value: originalHours.map(hour => hour.id),
          })
        );
        dispatch(showToast({ message: errorMessageFactory(error), type: 'danger' }));
      });
  };
}

export const duplicateWidget =
  (reference: actionTypes.WidgetReference): Thunk =>
  (dispatch, getState) => {
    const state: IState = getState();
    const referenceWidget = state.superreports.draft.widgets.find(
      widget => widget.id === reference || widget._ref === reference
    );
    if (!referenceWidget) return;

    const { id, _ref, ...baseWidget } = referenceWidget;
    baseWidget.sequence += 1;

    dispatch(addReportWidgetToDraft(baseWidget));
  };

export const fetchTimesheetData =
  (): Thunk<Promise<Array<Hour<unknown>>>> => async (dispatch, getState) => {
    const state = getState();
    const filteredQuery = getFilteredQueryInApiFormat(state);

    try {
      const { data } = await dispatch(
        actionTypes.wrapFetching(
          abortSignal => api.getTimesheetHours(filteredQuery, abortSignal),
          TIMESHEET_FETCH_KEY
        )
      );

      const maybeHasMore =
        data.length >= (filteredQuery.limit ?? HOUR_DETAILS_INITIAL_FILTER.limit);
      dispatch(actionTypes.fetchedTimesheetData(maybeHasMore));
      // TODO ConsolidateMemoryInteractions type this maybe?
      dispatch(
        ((filteredQuery.page ?? 1) === 1 ? hoursTypes.fetchedHours : hoursTypes.injectHours)(
          data as Array<Hour<LightProject | number>>
        )
      );
      return data;
    } catch (error) {
      if (isAbortError(error)) return Promise.reject();

      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      return error;
    }
  };

export const fetchTimesheetsForDatatable =
  (): Thunk<Promise<Array<Hour<unknown>>>> => async (dispatch, getState) => {
    const state = getState();
    const filteredQuery = getFilteredQueryInApiFormat(state);

    try {
      const { data, totalCount } = await dispatch(
        actionTypes.wrapFetching(
          abortSignal => api.getTimesheetHours(filteredQuery, abortSignal),
          TIMESHEET_FETCH_KEY
        )
      );

      const maybeHasMore =
        data.length >= (filteredQuery.limit ?? HOUR_DETAILS_INITIAL_FILTER.limit);
      dispatch(actionTypes.fetchedTimesheetData(maybeHasMore));
      dispatch(actionTypes.retrieveTotalHoursCount(totalCount));
      dispatch(hoursTypes.fetchedHours(data as Array<Hour<LightProject | number>>));

      return Promise.resolve(data);
    } catch (error) {
      if (isAbortError(error)) return Promise.reject();

      if (error?.reason === 'timeout') {
        dispatch(
          showToast({
            message: 'The requested timeframe is too large to load, please select a shorter range.',
            type: 'danger',
          })
        );
        return error;
      }

      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      return error;
    }
  };

export const fetchClientHourSummariesForDatatable =
  (
    isProjectData?: boolean,
    breakdownType: BreakdownType = 'day',
    interval_value: string = 'time'
  ): Thunk<Promise<Array<HourSummaryRow>>> =>
  async (dispatch, getState) => {
    const state = getState();
    // Use the clientReportQuery instead of the main query
    const clientReportQuery = state.superreports.clientReportQuery;

    clientReportQuery.sort;
    // Create a formatted query for the API that includes client_ids
    const filteredQuery = {
      ...clientReportQuery,
      filters: {
        ...clientReportQuery.filters,
        // Use a simplified version without tag flattening
        // to avoid the complexity of accessing the tags state
      },
      client_ids: clientReportQuery.client_ids,
      sort: clientReportQuery.sort || 'total',
      order: clientReportQuery.order || 'desc',
      limit: clientReportQuery.limit ?? HOUR_SUMMARY_INITIAL_FILTER.limit,
    };

    const isNextPage = (filteredQuery.page || 1) > 1;

    try {
      const { data, totalCount } = await dispatch(
        actionTypes.wrapFetching(
          abortSignal => {
            // Create request data with the interval parameter and interval_value
            return api.getTimesheetHours(filteredQuery, abortSignal, {
              interval: breakdownType,
              interval_value: interval_value,
            });
          },
          isProjectData ? 'project_summary' : TIMESHEET_FETCH_KEY
        )
      );

      const maybeHasMore =
        data.length >= (filteredQuery.limit ?? HOUR_SUMMARY_INITIAL_FILTER.limit);

      if (!isProjectData) {
        dispatch(
          hourSummaryTypes.fetchedHourSummary({
            data: data as unknown as Array<HourSummary>,
            append: isNextPage,
            maybeHasMore,
            totalCount,
          })
        );
      }

      return Promise.resolve(data);
    } catch (error) {
      if (isAbortError(error)) return Promise.reject();

      if (error?.reason === 'timeout') {
        dispatch(
          showToast({
            message: 'The requested timeframe is too large to load, please select a shorter range.',
            type: 'danger',
          })
        );
      }

      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      return error;
    }
  };

export const fetchDayPropertiesForTimesheets = (hours: Array<Hour<unknown>>): Thunk => {
  return dispatch => {
    if (!hours.length) {
      return Promise.resolve();
    }

    const dates = [...new Set(hours.map(h => h.day).filter(notNull))];
    const user_id = [...new Set(hours.map(h => h.user?.id).filter(notNull))];
    const params: DayPropertiesFilter = { dates, user_id };
    return dispatch(fetchDayPropertiesSmartly(params));
  };
};

export const exportTimesheetData =
  (
    format: SupportedExtensions,
    filter: HoursDetailsFilter,
    options: {
      totalsOnly?: boolean;
      entriesOnly?: boolean;
      resource?: actionTypes.ReportResources;
    } = { resource: 'hours' }
  ): Thunk<Promise<unknown>> =>
  async dispatch => {
    TrackingActions.exportedReport(actionTypes.ExportReportTrackingSource.Timesheets, format, {
      columns: filter.columns,
    });

    try {
      const response = await dispatch(
        actionTypes.wrapSaving(
          reportsApi.getReportExport(format, filter, false, { ...options, timesheet: true })
        )
      );
      return response;
    } catch (error) {
      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      return error;
    }
  };

export const fetchWidgetMetadata = () => async (dispatch: ThunkDispatch) => {
  const metadata = await dispatch(
    actionTypes.wrapFetching(reportsApi.getWidgetMetadata(), 'widgetMetadata')
  );
  dispatch(actionTypes.fetchedWidgetMetadata(metadata));
};

export const fetchPublicReport = (publicReportId: string) => async (dispatch: ThunkDispatch) => {
  const report = await dispatch(
    actionTypes.wrapFetching(reportsApi.getPublicReport(publicReportId))
  );
  dispatch(actionTypes.createReportDraft(newReportDraftFactory(report)));
  return report;
};

export const fetchPublicReportData =
  (publicReportId: string): Thunk =>
  async (dispatch, getState) => {
    const [response] = await Promise.all([
      dispatch(actionTypes.wrapFetching(reportsApi.getPublicReportData(publicReportId))),
      // We need to fetch currencies here to ensure they're ready before we set the totals
      dispatch(fetchCurrencies()),
    ]);

    const currencyLookup = getCurrenciesById(getState());

    const data = {
      ...createEmptyEntityDataResponse(),
      ...response,
      totals: getSummaryFromTotals(response.totals, currencyLookup),
    };

    return dispatch(actionTypes.fetchedReportData({ data, reload: true }));
  };

export const fetchPublicReportHoursFor =
  (params: FetchHoursForParams, publicReportId: string) => async (dispatch: ThunkDispatch) => {
    // ABORT MISSION, if we are collapsing the row -- no fetching done
    if (!params.expandRow) {
      return dispatch(
        actionTypes.collapseRowOnWidget({
          widgetReference: params.widgetReference,
          rowId: params.id,
        })
      );
    }

    // Otherwise, mark row as expanded
    dispatch(
      actionTypes.expandAndLoadRowOnWidget({
        widgetReference: params.widgetReference,
        rowId: params.id,
      })
    );

    try {
      const data = await reportsApi.getPublicReportHours(publicReportId);
      dispatch(
        actionTypes.fetchedWidgetHours({
          widgetReference: params.widgetReference,
          rowId: params.id,
          hasMoreResults: false, //TODO: Can backend tell us this? // data.length === query.limit,
          hourIds: data.map(hour => hour.id),
        })
      );

      dispatch(actionTypes.synthesizeProjectsFromHours(data));

      dispatch(hoursTypes.injectHours(data as unknown as Array<Hour<LightProject>>));
      return data;
    } catch (error) {
      debug.error(error);
      dispatch(actionTypes.fetchWidgetHoursError(error));
      return error;
    }
  };

export const clearReportFilters = (): Thunk => dispatch => {
  dispatch(actionTypes.setReportFilter(initialState.reportFilter));
};
export const clearSharedReportFilters = (): Thunk => dispatch => {
  dispatch(actionTypes.setPublicReportFilter(initialState.publicReportFilter));
};

export const getReportExportSettings =
  (reportId: number): Thunk<Promise<actionTypes.ReportExportSettingsPayload>> =>
  async dispatch => {
    const reportExportSettings = await dispatch(
      actionTypes.wrapFetching(reportsApi.getReportExportSettings(reportId))
    );
    if (!isEmpty(reportExportSettings)) {
      dispatch(actionTypes.fetchedReportExportSettings(reportExportSettings));
    }
    return reportExportSettings;
  };

export const saveReportExportSettings =
  (reportId: number, settings: actionTypes.ReportExportSettingsPayload): Thunk =>
  async dispatch => {
    const savedSettings = await dispatch(
      actionTypes.wrapSaving(reportsApi.saveReportExportSettings(reportId, settings))
    );

    dispatch(actionTypes.fetchedReportExportSettings(savedSettings));
    return savedSettings;
  };

export const fetchNextExportDate =
  (reportId: number, settings: actionTypes.ReportExportSettingsPayload): Thunk =>
  async dispatch => {
    const nextExportDate = await dispatch(
      actionTypes.wrapFetching(reportsApi.fetchNextExportDate(reportId, settings))
    );

    dispatch(actionTypes.fetchedNextExportDate(nextExportDate));
    return nextExportDate;
  };

export interface ExportPreviewData {
  totalCount: number;
  hours: Array<ExportPreviewRow>;
  forecastsById: Record<number, Forecast>;
}

export const fetchExportPreviewData =
  (): Thunk<Promise<ExportPreviewData>> => async (dispatch, getState) => {
    const state = getState();
    const filteredQuery = { ...getFilteredQueryInApiFormat(state), page: 1, limit: 5 };
    const userTimeFormat = getUserTimeFormat(state);
    const currencies = getCurrenciesById(state);
    const hourStates = getHourStates(state);
    const tags = getTagEntities(state);
    const teams = getTeams(state);

    const { data: rawHours, totalCount } = await dispatch(
      actionTypes.wrapFetching(
        abortSignal => api.getTimesheetHours(filteredQuery, abortSignal),
        `export-preview`
      )
    );

    const hours = rawHours.map(hour =>
      exportPreviewHourFactory(
        hour as unknown as Hour<LightProject>,
        userTimeFormat,
        currencies,
        hourStates,
        tags,
        teams
      )
    );

    const forecastIds = hours.reduce(
      (ids, hour) => {
        if (hour.forecastId) {
          ids.push(hour.forecastId);
        }
        return ids;
      },
      [] as Array<number>
    );

    let forecastsById = {};
    if (forecastIds.length) {
      const forecasts = await forecastApi.getForecasts({ forecast_ids: forecastIds });
      forecastsById = keyBy(forecasts, 'id');
    }

    return { hours, forecastsById, totalCount };
  };

interface ToggleSelectedHourParams {
  hourId: number;
  selected: boolean;
  shiftHeld: boolean;
  userId: number;
}
export const toggleSelectedHour =
  ({ hourId, selected: _maybeUseLater, shiftHeld, userId }: ToggleSelectedHourParams): Thunk =>
  (dispatch, getState) => {
    const userHours = getHoursAsCards(getState()).filter(hour => hour.user_id === userId);
    const {
      hourIds: selectedHourIds,
      rangeStartHourId,
      rangeEndHourId,
    } = getReportSelection(getState());
    const selectionForOtherUsers = selectedHourIds.filter(id =>
      userHours.every(hour => hour.id !== id)
    );
    const sortedByDayAndSequence = orderBy(
      userHours,
      [hour => cachedFromISO(hour.day), 'sequence'],
      ['asc', 'asc']
    );
    function setSelection<TKey extends keyof actionTypes.ReportSelection>(
      key: TKey,
      value: actionTypes.ReportSelection[TKey]
    ) {
      dispatch(actionTypes.batchSelectionActions.setSelection({ selectionKey: key, value }));
    }

    const singleSelect =
      rangeStartHourId == null ||
      !userHours.some(hour => hour.id === rangeStartHourId) ||
      !shiftHeld;

    if (singleSelect) {
      // Set the rangeStart and clear rangeEnd
      setSelection('rangeStartHourId', hourId);
      setSelection('rangeEndHourId', null);
      dispatch(
        actionTypes.batchSelectionActions.toggleSelection({
          selectionKey: 'hourIds',
          value: hourId,
        })
      );
    } else {
      // Handle range selection with shift key
      const startIndex =
        rangeStartHourId !== null
          ? sortedByDayAndSequence.findIndex(hour => hour.id === rangeStartHourId)
          : 0;
      const endIndex = sortedByDayAndSequence.findIndex(hour => hour.id === hourId);

      const range = sortedByDayAndSequence.slice(
        Math.min(startIndex, endIndex),
        Math.max(startIndex, endIndex) + 1
      );
      const newSelection = range.map(hour => hour.id);

      let rangeStartIndex = sortedByDayAndSequence.findIndex(hour => hour.id === rangeStartHourId);
      let rangeEndIndex = sortedByDayAndSequence.findIndex(
        hour => hour.id === (rangeEndHourId ?? hourId)
      );
      if (rangeStartIndex > rangeEndIndex) {
        [rangeStartIndex, rangeEndIndex] = [rangeEndIndex, rangeStartIndex];
      }
      if (rangeStartIndex >= 0 && rangeEndIndex >= 0) {
        const existingSelectionOutsideOuterRange = selectedHourIds.filter(hourId => {
          const index = sortedByDayAndSequence.findIndex(hour => hour.id === hourId);
          return index < rangeStartIndex || rangeEndIndex < index;
        });
        newSelection.push(...existingSelectionOutsideOuterRange);
      }

      newSelection.push(...selectionForOtherUsers);

      setSelection('hourIds', newSelection);
      setSelection('rangeEndHourId', hourId);
    }
  };
